import React from 'react';
import { StaticPage, TopbarContainer } from '..';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  FAQ,
  IconSuccess,
  IconTrailerShield,
} from '../../components';
import { LocationSearchForm } from '../../forms';
import { object } from 'prop-types';

import css from './TrailerShieldCustomerPage.module.css';
import trailerShieldLogo from '../../assets/trailer-shield-logo.png';

const TrailerShieldCustomerPageComponent = props => {
  const { history } = props;

  const handleSearchSubmit = values => {
    const { search, selectedPlace } = values.location;
    const { origin, bounds } = selectedPlace;
    const searchParams = { address: search, origin, bounds };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };

  return (
    <StaticPage
      title="Trailer Shield for Customers"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: 'Trailer Shield helps customers reduce their damage liability.',
        name: 'Trailer Shield - Customers',
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.staticPageWrapper}>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <div className={css.titleContainer}>
                  <h1 className={css.sectionTitle}>Reduce Your Liability with Trailer Shield</h1>
                  <img
                    src={trailerShieldLogo}
                    className={css.trailerShieldLogo}
                    alt="Trailer Shield Logo"
                  />
                </div>
                <div className={css.columns}>
                  <div className={css.sectionContentPitch}>
                    <p>
                      Even with the best care, accidents can happen — whether you’re towing,
                      tipping, or just parking. That’s why there’s Trailer Shield.
                    </p>
                    <p>
                      When booking a listing with Trailer Shield, you can choose from various levels
                      of cover to reduce your financial liability in case accidental damage occurs
                      during your hire.
                    </p>
                  </div>
                  <div>
                    <h2 className={css.sectionSubtitle}>Key Benefits</h2>
                    <div className={css.glanceSection}>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>
                          <b>Lower your liability</b> if there’s accidental damage
                        </span>
                      </div>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>
                          <b>Flexible cover options</b> to match your needs
                        </span>
                      </div>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>
                          <b>Hire with confidence</b> and peace of mind
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>Cover Options</h1>
                <ul className={css.shieldOptions}>
                  <li>
                    <IconTrailerShield size={60} className={css.iconLite} />
                    <b>Lite Shield</b>
                    <p>Basic protection at the lowest rate.</p>
                  </li>
                  <li>
                    <IconTrailerShield size={60} className={css.iconStandard} />
                    <b>Standard Shield</b>
                    <p>Balanced protection with moderate excess.</p>
                  </li>
                  <li>
                    <IconTrailerShield size={60} className={css.iconFull} />
                    <b>Full Shield</b>
                    <p>Maximum cover, minimal excess.</p>
                  </li>
                  <li>
                    <IconTrailerShield size={60} className={css.iconOptOut} />
                    <b>Opt Out</b>
                    <p>No cover — you're fully liable in case of an accident.</p>
                  </li>
                </ul>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>How to Choose Trailer Shield</h1>
                <p>
                  When browsing search results, look for trailers with the{' '}
                  <IconTrailerShield size={26} /> <b>Roadside & Trailer Shield</b> tag to see cover
                  options.
                </p>
                <p>
                  During the booking process, you can select your preferred level of Trailer Shield
                  damage cover before proceeding to payment.
                </p>
                <p>
                  If you opt out of Trailer Shield, you’ll be responsible for the full cost of any
                  accidental damage.
                </p>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>Important Note</h1>
                <div className={css.importantNote}>
                  <p>
                    <b>Trailer Shield isn’t available on every trailer.</b> Look for the{' '}
                    <IconTrailerShield size={30} /> <b>Roadside & Trailer Shield</b> tag when
                    browsing listings.
                  </p>
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>FAQs</h1>
                <div className={css.faqSection}>
                  <FAQ category={'trailerShieldCustomer'} />
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <div className={css.readySection}>
                  <div className={css.readySectionText}>
                    <h1 className={css.sectionTitle}>Ready to hire?</h1>
                    <p>
                      Choose a listing with <IconTrailerShield size={26} />{' '}
                      <b>Roadside & Trailer Shield</b> for extra protection and peace of mind.
                    </p>
                  </div>
                  <div className={css.readySectionButton}>
                    <div className={css.searchFormForHirersContainer}>
                      <LocationSearchForm
                        className={css.searchForm}
                        onSubmit={handleSearchSubmit}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

TrailerShieldCustomerPageComponent.propTypes = {
  history: object.isRequired,
};

const TrailerShieldCustomerPage = compose(withRouter)(TrailerShieldCustomerPageComponent);

export default TrailerShieldCustomerPage;
